/**
 * LINE ログイン（FR-117・06 §9・03 §4.3）＝**LIFF の ID トークンを backend に渡すだけ**。
 *
 * 経路は1本：`liff.init()` →（未ログインなら `liff.login()`）→ `liff.getIDToken()`
 *   → `POST /api/v1/auth/liff` → 短命JWT（1h・メモリ保持＝02 §4.2）。
 * backend は LINE の verify エンドポイントで署名・`aud`（＝LINE Login チャネルID）・exp を
 * 検証する（`identity/providers.py`）。**このファイルは検証しない**＝クライアント側の判定を
 * 信用する作りにしない。
 *
 * **SDK は動的 import**：`config.liffId` が空（dev＝DEV_FAKE_LIFF）の経路では 1 バイトも読み込まない。
 *
 * ここで踏んだ落とし穴（レビュー指摘・すべて実測で確認済み）：
 * 1. **StrictMode の二重実行で永久に固まる**：ref で「1回だけ」を守りつつ cleanup で
 *    `alive=false` にすると、2回目の setup が早期 return するため**新しい生存フラグが作られず**、
 *    以後 `setPhase` も `onSuccess` も呼ばれない（成功しても token を捨てる）。
 *    → **開始の重複防止と「結果を捨てるか」を分ける**（`started` ref ＋ `mounted` ref）。
 * 2. **キャンセルすると同じ URL では二度と入れない**：LINE は
 *    `?error=access_denied&…&liffOAuth2Error=true` を付けて戻し、`liff.init()` はそれを見て throw する。
 *    リトライが `location.reload()` だと同じクエリのままなので何度でも失敗する。
 *    → **認可の痕跡を消したクリーン URL** へ遷移する（SDK が消すのは `liff*` だけ）。
 * 3. **ID トークンの期限切れから戻れない**：`getIDToken()` はストアの値を返すだけで更新されず、
 *    `isLoggedIn()` は期限を見ない。→ 401 のときは `liff.logout()` してから login し直す（1回だけ）。
 */

import { ApiError, auth, type PartnerToken } from "@kenzai/api-client";
import { AuthCard, Button, FormError } from "@kenzai/ui";
import { useCallback, useEffect, useRef, useState } from "react";

/** login() で離脱した往復を数える印。戻ってきて成功したら消す。 */
const RETRY_FLAG = "kz.liff.login.attempted";
/** 期限切れ検知で logout→login をやり直した印（1回だけ）。 */
const RELOGIN_FLAG = "kz.liff.relogin.attempted";

type Phase = { kind: "working" } | { kind: "error"; message: string; detail?: string };

/** sessionStorage が使えない環境（プライベート/埋め込み）でも落とさない。 */
const flag = {
  get(key: string): boolean {
    try {
      return sessionStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },
  set(key: string): void {
    try {
      sessionStorage.setItem(key, "1");
    } catch {
      /* 使えなくても続行（往復ガードが弱まるだけ） */
    }
  },
  clear(...keys: string[]): void {
    try {
      for (const k of keys) sessionStorage.removeItem(k);
    } catch {
      /* 同上 */
    }
  },
};

/**
 * 認可の痕跡を落とした「入口の URL」。
 * `redirectUri` は LIFF の エンドポイントURL と**前方一致**である必要があるので、
 * パス（＝招待リンクの `/invite` など）は保つ。
 *
 * ★`code` は LINE の認可コードと**同名**なので、アプリ側のクエリに `code` を使ってはいけない
 *   （SDK の parse が重複キーを配列にし、トークン交換が壊れる）。招待コードの事前充填は
 *   `invite` を使う（06 §2.2 の設計側も合わせて改める）。
 */
function cleanUrl(): string {
  const url = new URL(window.location.href);
  for (const k of [
    "code",
    "state",
    "error",
    "error_description",
    "errorCode",
    "errorMessage",
    "liffOAuth2Error",
    "liffClientId",
    "liffRedirectUri",
    "liff.state",
    // ★**`invite` は消さない**（06 §2.2・T-D30）＝招待リンクのプレフィル値。ここに足すと
    //   LINE ログインの往復のたびにコードが消え、招待リンクの意味が無くなる。
  ]) {
    url.searchParams.delete(k);
  }
  url.hash = "";
  return url.toString();
}

/** 例外を「利用者に出す文言」と「技術的な手掛かり」に割る（規約9＝内部情報を垂れ流さない）。 */
function describe(err: unknown): { message: string; detail?: string } {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return { message: "ログインの有効期限が切れました。もう一度お試しください。" };
    }
    // ★上流の英語文言をそのまま職人に見せない（`channel not found` 等が出ていた）
    return {
      message: "ログインできませんでした。時間をおいてもう一度お試しください。",
      detail: err.requestId ? `お問い合わせ番号: ${err.requestId}` : undefined,
    };
  }
  return { message: "LINE ログインを開始できませんでした。時間をおいてもう一度お試しください。" };
}

export function LiffSignIn({
  liffId,
  onSuccess,
}: {
  liffId: string;
  onSuccess: (t: PartnerToken) => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: "working" });
  // 「開始したか」と「まだ画面に居るか」は**別物**（上記の落とし穴 1）。
  const started = useRef(false);
  const mounted = useRef(true);
  // onSuccess が毎レンダー変わっても effect を再実行させない（最新を ref で持つ）
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const retry = useCallback(() => {
    flag.clear(RETRY_FLAG, RELOGIN_FLAG);
    // reload ではなく**クリーン URL へ遷移**（落とし穴 2）
    window.location.replace(cleanUrl());
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        const liff = (await import("@line/liff")).default;
        await liff.init({ liffId });

        if (!liff.isLoggedIn()) {
          if (flag.get(RETRY_FLAG)) {
            flag.clear(RETRY_FLAG);
            if (mounted.current) {
              setPhase({
                kind: "error",
                message: "LINE ログインが完了しませんでした。",
                detail: "もう一度お試しください。続く場合は担当者へご連絡ください。",
              });
            }
            return;
          }
          flag.set(RETRY_FLAG);
          liff.login({ redirectUri: cleanUrl() });
          return; // 離脱するのでここから先は動かない
        }

        const idToken = liff.getIDToken();
        if (!idToken) {
          flag.clear(RETRY_FLAG);
          if (mounted.current) {
            setPhase({
              kind: "error",
              message: "LINE の情報を取得できませんでした。",
              detail: "設定を確認しますので、担当者へご連絡ください。",
            });
          }
          return;
        }

        try {
          const token = await auth.liff(idToken);
          flag.clear(RETRY_FLAG, RELOGIN_FLAG);
          if (mounted.current) onSuccessRef.current(token);
        } catch (err) {
          // ★期限切れの ID トークンは `getIDToken()` では更新されない（落とし穴 3）。
          //   一度だけ logout→login でやり直す（無限往復にしない）。
          if (err instanceof ApiError && err.status === 401 && !flag.get(RELOGIN_FLAG)) {
            flag.set(RELOGIN_FLAG);
            liff.logout();
            liff.login({ redirectUri: cleanUrl() });
            return;
          }
          throw err;
        }
      } catch (err) {
        flag.clear(RETRY_FLAG);
        if (mounted.current) setPhase({ kind: "error", ...describe(err) });
      }
    })();

    return () => {
      mounted.current = false;
    };
  }, [liffId]);

  if (phase.kind === "working") {
    return (
      <AuthCard title="建材オーダー" subtitle="LINE でログインしています…">
        <p style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
          そのままお待ちください。
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="建材オーダー">
      <FormError message={phase.message} />
      {phase.detail && (
        <p
          data-testid="liff-error-detail"
          style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}
        >
          {phase.detail}
        </p>
      )}
      <Button fullWidth onClick={retry}>もう一度試す</Button>
    </AuthCard>
  );
}
